import { File } from "@/src/store/type";
import { DubboError } from "@apachedubbo/dubbo";
import { ReadonlyURLSearchParams } from "next/navigation";
import { days, Events, fileBaseURL } from "./../config";
import { HttpError } from "./HttpError";
import { RpcError } from "./RpcError";
// import refreshTokenStore from "@/src/store/server/cargogo/auth/refreshTokenStore";
import { deleteCookie } from "cookies-next/client";
import { DayString } from "../type";
import { publish } from "./event";

export const isEmailValid = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email) ? true : false;
};

export const isNumeric = (str: string): boolean => {
  if (typeof str != "string") return false;
  return !isNaN(Number(str)) && !isNaN(parseFloat(str));
};

export const isDateValid = (value: string): boolean => {
  if (!value) return false;
  const isoDateTime =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(\.\d{1,6})?)?(Z|([+\-])(\d{2}):?(\d{2}))$/;
  const m = value.match(isoDateTime);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const date = new Date(value);
    if (isNaN(date.getTime())) return false;
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }
  return false;
};

export const formatDate = (date?: Date): string => {
  if (!date || isNaN(date.getTime())) return "";
  const local = new Date(date);
  const mm = String(local.getMonth() + 1).padStart(2, "0");
  const dd = String(local.getDate()).padStart(2, "0");
  const yyyy = local.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/** Business calendar timezone (matches BE BusinessDates / FE date pickers). */
export const BUSINESS_TIMEZONE = "Asia/Hong_Kong";

/**
 * Today's calendar date in Asia/Hong_Kong as `yyyy-MM-dd`.
 * Do not use `toISOString().split("T")[0]` — that is UTC and is wrong before 08:00 HKT.
 */
export const getTodayDate = (): string => {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
};

/**
 * Start of today's HKT calendar day as UTC ISO instant.
 * Use for Mongo `validPeriodEnd $gte` against FE-stored HKT-midnight instants
 * (e.g. UI 03/08/2026 → `2026-08-02T16:00:00.000Z`).
 */
export const getBusinessDayStartIso = (): string => {
  return businessDayStartIso(getTodayDate());
};

/** `yyyy-MM-dd` (already) or ISO instant → HKT calendar `yyyy-MM-dd`. */
export const toBusinessDateOnly = (value?: string | Date | null): string => {
  if (value == null || value === "") return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    if (typeof value === "string" && value.includes("T")) {
      return value.split("T")[0] ?? "";
    }
    return typeof value === "string" ? value : "";
  }
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
};

/** HKT calendar day `yyyy-MM-dd` → UTC ISO at that day's 00:00+08:00. */
export const businessDayStartIso = (yyyyMmDd: string): string => {
  if (!yyyyMmDd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return "";
  return new Date(`${yyyyMmDd}T00:00:00+08:00`).toISOString();
};

/** HKT calendar day `yyyy-MM-dd` → UTC ISO at that day's 23:59:59.999+08:00. */
export const businessDayEndIso = (yyyyMmDd: string): string => {
  if (!yyyyMmDd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return "";
  return new Date(`${yyyyMmDd}T23:59:59.999+08:00`).toISOString();
};

/** Filter instant → end of its HKT calendar day. Empty stays empty. */
export const endOfBusinessDayIso = (value?: string): string => {
  const day = toBusinessDateOnly(value);
  return day ? businessDayEndIso(day) : "";
};

/** HKT wall-clock `yyyy-MM-dd` + `HH:mm` → UTC ISO. */
export const businessDateTimeToIso = (yyyyMmDd: string, hhMm: string): string => {
  if (!yyyyMmDd) return "";
  const safeTime = /^([01]\d|2[0-3]):[0-5]\d$/.test(hhMm) ? hhMm : "00:00";
  return new Date(`${yyyyMmDd}T${safeTime}:00+08:00`).toISOString();
};

/** Format instant as `HH:mm` in HKT. */
export const toBusinessTimeHm = (value?: string | null): string => {
  if (!value) return "00:00";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "00:00";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${hour}:${minute}`;
};

/**
 * Convert date from DD/MM/YYYY format to YYYY-MM-DD (ISO) format
 * @param date - Date string in DD/MM/YYYY format
 * @returns Date string in YYYY-MM-DD format or empty string if input is invalid
 */
export const convertDDMMYYYYToISODate = (date: string): string => {
  if (!date) return "";
  const [day, month, year] = date.split("/");
  if (!day || !month || !year) return date;
  return `${year}-${month}-${day}`;
};

/**
 * Convert date from DD/MM/YYYY format to ISO 8601 datetime format
 * @param date - Date string in DD/MM/YYYY format or ISO string
 * @param time - Optional time in HH:MM:SS format (defaults to 00:00:00)
 * @returns Date string in ISO 8601 format (YYYY-MM-DDTHH:MM:SS.000Z) or empty string if input is invalid
 */
export const convertDDMMYYYYToISO8601 = (date: string, time: string = "00:00:00"): string => {
  if (!date) return "";

  // If already a valid ISO string, return as-is
  if (isDateValid(date)) {
    return date;
  }

  // Otherwise, parse DD/MM/YYYY format
  const [day, month, year] = date.split("/");
  if (!day || !month || !year) return date;

  const isoDate = `${year}-${month}-${day}`;
  const timeWithMS = time.includes(".") ? time : `${time}.000`;

  return `${isoDate}T${timeWithMS}Z`;
};

export const formatDateString = (date?: string): string => {
  if (!date || !isDateValid(date)) return "";
  const local = new Date(date);
  const mm = String(local.getMonth() + 1).padStart(2, "0");
  const dd = String(local.getDate()).padStart(2, "0");
  const yyyy = local.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/** List/display validity: both → `from ~ to`; only start → `from ~`; only end → `~ to`. */
export const formatValidityRange = (from?: string, to?: string): string => {
  if (from && to) return `${from} ~ ${to}`;
  if (from) return `${from} ~`;
  if (to) return `~ ${to}`;
  return "";
};

/**
 * Convert day string to number (e.g., "mon" -> 1)
 * @param day - Day of the week as a string ("mon", "tue", "wed", "thu", "fri", "sat", "sun")
 * @return number corresponding to the day of the week, or undefined if input is invalid
 */
export const getDayNumber = (day: DayString): number | undefined => {
  return days.find((d) => d.day === day)?.value;
};

/**
 * Convert day number to string (e.g., 1 -> "mon")
 * @param num - Number representing the day of the week (1 for Monday, 2 for Tuesday, ..., 7 for Sunday)
 * @return Day of the week as a string, or undefined if input is invalid
 */
export const getDayString = (num: number): DayString | undefined => {
  return days.find((d) => d.value === num)?.day;
};

export const formatNumber = (value: string | number | null | undefined, decimal: number = 2) => {
  if (value === null || value === undefined || value === "") return "";
  const source =
    typeof value === "string"
      ? value
          .replace(/\uFF0C/g, ",")
          .replace(/(\d{1,3}(?:,\s*\d{3})+)/g, (group) => group.replace(/[,\s]/g, ""))
          .replace(/,/g, "")
      : value;
  const num = typeof source === "string" ? parseFloat(source) : source;
  if (isNaN(num)) return "";

  const [intPart, decimalPart = ""] = `${num}`.split(".");
  const formattedInt = parseInt(intPart, 10).toLocaleString("en-US");

  if (`${num}`.includes(".")) {
    if (decimalPart.length === 0) return `${formattedInt}.`;
    if (decimalPart.length <= decimal) return `${formattedInt}.${decimalPart}`;
    return `${formattedInt}${decimal > 0 ? "." : ""}${decimalPart.slice(0, decimal)}`;
  }
  return formattedInt;
};

export const toNum = (v: unknown): number => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return isNaN(v) ? 0 : v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }
  return 0;
};

export const parseISODateStringToDate = (isoDate: string): Date | undefined => {
  if (!isoDate) return undefined;
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? undefined : date;
};

/**
 * Convert date from YYYY-MM-DD format to MM/DD/YYYY format
 * @param isoDate - Date string in YYYY-MM-DD format
 * @returns Date string in MM/DD/YYYY format or empty string if input is invalid
 */
export const convertISODateToUSFormat = (isoDate: string): string => {
  if (!isoDate) return "";

  const regex = /^(\d{4})-(\d{2})-(\d{2})$/;
  const match = isoDate.match(regex);
  if (!match) return isoDate; // Return original if not in expected format

  const [_, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

/**
 * Convert date from YYYY-MM-DD format to DD/MM/YYYY format
 * @param isoDate - Date string in YYYY-MM-DD format
 * @returns Date string in DD/MM/YYYY format or empty string if input is invalid
 */
export const convertISODateToEUFormat = (isoDate: string): string => {
  if (!isoDate) return "";

  const regex = /^(\d{4})-(\d{2})-(\d{2})$/;
  const match = isoDate.match(regex);
  if (!match) return isoDate; // Return original if not in expected format

  const [_, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

export const areSetsEqual = (a: Set<any>, b: Set<any>) => {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
};

export const deleteAuthCookies = (
  event: string | undefined = Events.Logout,
  msg?: string | undefined,
) => {
  deleteCookie("access-token");
  if (msg) {
    publish(event, { message: msg });
  }
};

export { failAuthSession, invalidateAuthSession, isAuthSessionInvalidated, resetAuthSessionInvalidation } from "./auth-session";

export const isTokenExpired = (token?: string): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp;
    console.log(
      `[DEBUG] Token expired date ${new Date(exp * 1000).toISOString()} at ${new Date().toISOString()}`,
    );
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

export function getHttpError(code: number, message: string): HttpError {
  const msg = message ? message : getErrorMessage(code);
  return new HttpError(code, msg);
}

/** Gateway/proxy HTML error pages surface as JSON parse failures in dubbo-web. */
function isGatewayHtmlErrorMessage(message: string | undefined): boolean {
  if (!message) return false;
  return (
    /Unexpected token\s*['"`]?</i.test(message) ||
    /<\/?html[\s>]/i.test(message) ||
    /is not valid JSON/i.test(message)
  );
}

export function getRpcError(e: DubboError): RpcError {
  const code = e.code as number;
  const message = e.rawMessage as string;

  if (isGatewayHtmlErrorMessage(message)) {
    return new RpcError(502, "BAD_GATEWAY", "error.request.gateway_timeout");
  }

  switch (code) {
    case 1:
      return new RpcError(499, "CANCELLED", message || "error.request.cancelled");
    case 2:
      return new RpcError(500, "UNKNOWN", message || "error.request.unexpected");
    case 3:
      return new RpcError(400, "INVALID_ARGUMENT", message || "error.request.invalid_argument");
    case 4:
      return new RpcError(504, "DEADLINE_EXCEEDED", message || "error.request.gateway_timeout");
    case 5:
      return new RpcError(404, "NOT_FOUND", message || "error.request.not_found");
    case 7:
      return new RpcError(403, "PERMISSION_DENIED", message || "error.request.permission_denied");
    case 8:
      return new RpcError(429, "RESOURCE_EXHAUSTED", message || "error.request.too_many_attempts");
    case 13:
      return new RpcError(500, "INTERNAL", message || "error.request.internal");
    case 14:
      return new RpcError(503, "UNAVAILABLE", message || "error.request.server_error");
    case 16:
      return new RpcError(401, "UNAUTHENTICATED", message || "error.request.unauthorized");
    default:
      return new RpcError(500, "UNHANDLED", message || "error.request.unknown_error");
  }
}

export const getErrorMessage = (errorCode: number): string => {
  switch (errorCode) {
    case 400:
      return "error.request.bad_request";
    case 401:
      return "error.request.unauthorized";
    case 403:
      return "error.request.permission_denied";
    case 404:
      return "error.request.not_found";
    case 429:
      return "error.request.too_many_attempts";
    case 500:
      return "error.request.server_error";
    case 502:
    case 503:
    case 504:
      return "error.request.gateway_timeout";
    default:
      return "error.request.unknown_error";
  }
};

export const getDubboErrorMessage = (error: unknown): string => {
  if (error instanceof DubboError) {
    const e = DubboError.from(error);
    return e.message;
  } else if (error instanceof Error) {
    return error.message;
  } else if (typeof error === "string") {
    return error;
  } else {
    return "common.unknown_error";
  }
};

export const download = (file: { content: Uint8Array; name: string; type: string }) => {
  try {
    const content = new Uint8Array(file.content);
    const blob = new Blob([content], { type: file.type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 300);
  } catch (err) {
    console.error("[ERR] processing file download:", err);
  }
};

export const getFile = async (files: FileList | null, name: string | undefined | null) => {
  try {
    if (!files || files.length === 0) {
      return null;
    }
    if (name) {
      const foundIndex = Array.from(files).findIndex(
        (x) => x.name.toLowerCase() === name.toLowerCase(),
      );
      const file = files[foundIndex];
      if (!file) {
        return null;
      }
      const buffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ""),
      );
      const content = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      return {
        content: content,
        name: file.name,
        type: file.type,
      } as File;
    } else {
      const file = files[0];
      if (!file) {
        return null;
      }
      const buffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ""),
      );
      const content = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      return {
        content: content,
        name: file.name,
        type: file.type,
      } as File;
    }
  } catch (err) {
    console.error("[ERR] get file failed:", err);
  }
};

export const updateSearchParams = (
  searchParams: ReadonlyURLSearchParams,
  updates: Record<string, string>,
  removeKeys: string[] = [],
) => {
  const params = new URLSearchParams(searchParams.toString());
  Object.entries(updates).forEach(([key, value]) => {
    params.set(key, value);
  });
  removeKeys.forEach((key) => {
    params.delete(key);
  });
  return params.toString();
};

export const formatCurrency = (value: string) => {
  if (!value || value.trim() === "") {
    return "";
  }
  const numberValue = parseFloat(value.replace(/[^0-9.]/g, "")); // Parse as float
  if (isNaN(numberValue)) {
    return ""; // Handle invalid numbers
  }
  return `$${numberValue.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
};

export const parseDateStringToDate = (string: string): Date | undefined => {
  if (!string) return undefined;
  const [day, month, year] = string.split("/").map(Number);
  return new Date(year, month - 1, day);
};

export const parseDate = (str: string) => {
  const [day, month, year] = str.split("/");
  return new Date(`${year}-${month}-${day}`);
};

export function getOptionLabelByValue<T extends { value: string; label: string }>(
  options: T[] | undefined,
  value: string | number | undefined,
): string {
  return options?.find((opt) => opt.value === value)?.label ?? "";
}

export const getEnumKey = (e: Record<string, number | string>, value: number): string | undefined => {
  return Object.keys(e).find((key) => Number.isNaN(Number(key)) && e[key] === value);
};
/**
 * Convert enum key string to enum value, with optional fallback
 * @param eo - The enum object
 * @param key - The string key to look up
 * @param fallback - Optional fallback value if key not found
 * @returns The enum value, fallback, or undefined
 *
 * @example
 * getEnumValue(Zone, "ASIA", Zone.NA) // returns Zone.ASIA
 * getEnumValue(Zone, undefined, Zone.NA) // returns Zone.NA
 * getEnumValue(Zone, "INVALID") // returns undefined
 */
export const getEnumValue = <T extends Record<string, any>>(
  eo: T,
  key: string | undefined,
  fallback?: T[keyof T],
): T[keyof T] | undefined => {
  if (!key) return fallback;
  if (key in eo) return eo[key as keyof T];
  return fallback;
};

export const setHTMLFieldValue = (
  container: HTMLElement,
  selector: string,
  value: string,
): void => {
  let el: Element | null = container.querySelector(`[data-field="${selector}"]`);

  if (!el) {
    const all = Array.from(container.querySelectorAll("[data-field]"));
    const lower = selector.toLowerCase();
    for (const e of all) {
      const v = (e.getAttribute("data-field") || "").trim();
      if (!v) continue;
      if (v === selector || v.toLowerCase() === lower) {
        el = e;
        break;
      }
      const parts = v.split(".");
      if (parts[parts.length - 1].toLowerCase() === lower) {
        el = e;
        break;
      }
    }
  }

  if (!el) el = container.querySelector(`#${selector}`);

  if (el) {
    if ((el as HTMLImageElement).tagName?.toLowerCase() === "img") {
      try {
        (el as HTMLImageElement).src = value;
      } catch {
        (el as HTMLElement).textContent = value;
      }
    } else {
      (el as HTMLElement).textContent = value;
    }
  }
};

export const normalizeImageSrc = (value: string) => {
  const raw = (value || "").trim();
  if (!raw) return "";

  // Keep fully-qualified and data/blob URLs as-is.
  if (/^(https?:\/\/|data:|blob:)/i.test(raw)) return raw;

  const normalizedPath = `/${raw.replace(/^\/+/, "")}`;
  const base = (fileBaseURL || "").trim();

  if (!base || base === "/") return normalizedPath;

  return `${base.replace(/\/+$/, "")}${normalizedPath}`;
};
